import { PrintOnDemandProductJsonldPrintOnDemandProductRead } from '@/api/generated';
import CustomMarkdown from '@/molecules/CustomMarkdown';
import TableLine from '@/molecules/TableLine';
import TabledContent from '@/molecules/TableLine/TableContent';
import { CURRENCY_API_CODE } from '@/types/modifiers/references';
import { CARDS_PER_PRINTING_PAGE } from 'constants/printing';
import ModalClassicLayout from 'context/Modal/layout/Classic';
import { useFormatPrice } from 'hooks/i18n';
import { useTranslation } from 'react-i18next';

type PricingOffersProps = {
  closeModal: () => void;
  title: string;
  submitLabel?: string;
  offers: PrintOnDemandProductJsonldPrintOnDemandProductRead[];
};

type FormattedPack = {
  key: string;
  rangeMin: number;
  rangeMax: number;
  packLabel: string;
  price: string;
  pricePerCard: string;
  additionalInfo?: string;
};

const PACK_RANGES = {
  SINGLE: { min: 1, max: 2 },
  SMALL: { min: 3, max: 4 },
  MEDIUM: { min: 5, max: 7 },
  LARGE: { min: 8, max: 9 },
  XLARGE: { min: 10, max: 12 },
  XXLARGE: { min: 13, max: 15 },
} as const;

const PricingOffers = ({ closeModal, title, submitLabel, offers }: PricingOffersProps) => {
  const { t } = useTranslation();
  const { currency = CURRENCY_API_CODE.USD, formatUserCurrency } = useFormatPrice();
  const prices = offers[0]?.prices;

  const groupedPrices = Object.values(PACK_RANGES)
    .map((range) => {
      const pricesInRange = prices?.filter(
        (price) => price.quantity! >= range.min && price.quantity! <= range.max && price.currency === currency,
      );

      if (!pricesInRange?.length) {
        return null;
      }

      const firstPrice = pricesInRange[0];

      return {
        key: `${range.min}-${range.max}`,
        rangeMin: range.min,
        rangeMax: range.max,
        packLabel: t('printing-packs', { min: range.min, max: range.max }),
        price: t('printing-pack-price', { price: formatUserCurrency(firstPrice.price!) }),
        pricePerCard: t('printing-price-per-card', {
          price: formatUserCurrency(firstPrice.price! / CARDS_PER_PRINTING_PAGE),
        }),
        additionalInfo:
          range.min >= 20 ? t('printing-price-shipping-gift', { price: formatUserCurrency(20) }) : undefined,
      };
    })
    .filter(Boolean) as Array<FormattedPack>;

  return (
    <ModalClassicLayout closeModal={closeModal} submitLabel={submitLabel} title={title} alert>
      <div className="flex flex-col gap-6">
        <p className="text-neutral-900">{t('printing-price-description')}</p>

        <div className="flex flex-col gap-2">
          {groupedPrices.map((price) => (
            <TableLine key={price.key} label={price.packLabel}>
              <TabledContent
                additionalInfo={price.additionalInfo}
                price={price.price}
                pricePerCard={price.pricePerCard}
              />
            </TableLine>
          ))}
        </div>
        <CustomMarkdown className="text-neutral-400">{t('printing-price-additional-info')}</CustomMarkdown>
      </div>
    </ModalClassicLayout>
  );
};

export default PricingOffers;
